import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  Copy,
  Loader2,
  MoreHorizontal,
  ThumbsDown,
  ThumbsUp,
  Trash2,
} from "lucide-react";
import type { UIMessage } from "ai";
import type {
  FeedbackReasonCode,
  FeedbackRecord,
} from "@graftus/schema";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTRPC } from "@/trpc/client";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { AiMark } from "@/components/ui/ai-mark";
import { ChatBubble } from "@/components/ui/chat-bubble";
import { ThinkingIndicator } from "./ThinkingIndicator";
import { cn } from "@/lib/utils";
import { LogEvents, track } from "@/analytics/track";
import { useProfile } from "@/context/ProfileContext";
import { Markdown, type IntelligenceMemberRoster } from "./markdown";
import type { ChatScope } from "./scope";
import type { TopicKey } from "./topics";
import { ToolCallGroup } from "./ToolCallGroup";
import { DataParts } from "./data-parts/DataParts";
import { parseAssistantParts } from "./chat-tool-utils";

export type ChatMessageMetadata = {
  topicKey?: string;
  promptVersion?: number;
  modelId?: string;
  contextFingerprint?: string;
  /** The LangSmith run behind this answer; votes are attached to it. */
  runId?: string;
  /** Server had pulse in scope; does not mean the pulse tool was invoked. */
  pulseInContext?: boolean;
};
export type ChatUIMessage = UIMessage<ChatMessageMetadata>;

export interface FeedbackContext {
  businessId: string;
  threadId: string | null;
  scope: ChatScope;
  topicKey: TopicKey;
}

interface Props {
  message: ChatUIMessage;
  isStreaming?: boolean;
  onDelete?: () => void;
  feedback?: FeedbackRecord;
  feedbackContext?: FeedbackContext;
  /** For assistant markdown: member chips + name fallback. */
  businessId?: string;
  memberRoster?: IntelligenceMemberRoster | null;
  /**
   * The chat is already showing an error banner for this turn, so an empty
   * assistant message must not add its own "could not complete" line.
   */
  suppressEmptyFallback?: boolean;
}

export function ChatMessage({
  message,
  isStreaming,
  onDelete,
  feedback,
  feedbackContext,
  businessId,
  memberRoster,
  suppressEmptyFallback,
}: Props) {
  const { t } = useTranslation("intelligence");
  const fullText = message.parts
    .map((p) => (p.type === "text" ? p.text : ""))
    .join("");
  const isUser = message.role === "user";

  if (isUser) {
    return <ChatBubble variant="user">{fullText}</ChatBubble>;
  }

  const parsed = parseAssistantParts(message, { isStreaming: Boolean(isStreaming) });
  const text = parsed.textContent;

  return (
    <div className="group/msg flex flex-col gap-1.5">
      <div className="max-w-[92%]">
        {!parsed.hasContent && !parsed.showThinking ? (
          // When only hidden tools ran (e.g. provide_suggestions), the model did
          // background work whose output appears elsewhere — don't show an error.
          // When the chat shows an error banner, that banner is the message.
          parsed.hasOnlyHiddenTools || suppressEmptyFallback ? null : (
            <span className="text-sm text-muted-foreground">
              {t("tool.actionFailed")}
            </span>
          )
        ) : (
          <div className="flex flex-col gap-3">
            <AiMark className="self-start" />
            {parsed.showThinking && <ThinkingIndicator />}
            {text ? (
              <ChatBubble variant="anser">
                <Markdown
                  text={text}
                  businessId={businessId}
                  members={memberRoster}
                  isStreaming={isStreaming}
                />
                {isStreaming && (
                  <span
                    className="mt-1 inline-block h-4 w-[2px] rounded-sm bg-foreground/60 animate-caret"
                    aria-hidden
                  />
                )}
              </ChatBubble>
            ) : null}
            {parsed.dataParts.length > 0 ? (
              <DataParts
                parts={parsed.dataParts}
                businessId={businessId}
                roster={memberRoster}
              />
            ) : null}
            {parsed.visibleTools.length > 0 ? (
              <ToolCallGroup parts={parsed.visibleTools} businessId={businessId} />
            ) : null}
          </div>
        )}
      </div>
      {!isStreaming && text && (
        <MessageActions
          text={text}
          messageId={message.id}
          metadata={message.metadata}
          feedback={feedback}
          feedbackContext={feedbackContext}
          onDelete={onDelete}
        />
      )}
    </div>
  );
}

function MessageActions({
  text,
  messageId,
  metadata,
  feedback,
  feedbackContext,
  onDelete,
}: {
  text: string;
  messageId: string;
  metadata: ChatMessageMetadata | undefined;
  feedback: FeedbackRecord | undefined;
  feedbackContext: FeedbackContext | undefined;
  onDelete?: () => void;
}) {
  const { t } = useTranslation("intelligence");
  const trpc = useTRPC();
  const qc = useQueryClient();
  const { memberRole } = useProfile();
  const isManager = memberRole === "owner" || memberRole === "admin";

  const reasonChoices = useMemo(
    (): Array<{ code: FeedbackReasonCode; label: string }> => [
      { code: "incorrect", label: t("message.feedback.incorrect") },
      { code: "wrong_person", label: t("message.feedback.wrongPerson") },
      { code: "vague", label: t("message.feedback.vague") },
      { code: "format", label: t("message.feedback.format") },
      { code: "unsafe", label: t("message.feedback.unsafe") },
    ],
    [t],
  );

  const submit = useMutation(
    trpc.salesTeams.submitMessageFeedback.mutationOptions(),
  );
  const clear = useMutation(
    trpc.salesTeams.clearMessageFeedback.mutationOptions(),
  );

  // Optimistic local state mirrors the persisted record so the UI reacts
  // before the round-trip completes. Reset whenever the persisted record
  // changes (e.g. after invalidation) so we don't drift.
  const [optimistic, setOptimistic] = useState<FeedbackRecord | null>(
    feedback ?? null,
  );
  useEffect(() => {
    setOptimistic(feedback ?? null);
  }, [feedback]);

  const [copied, setCopied] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reasonCodes, setReasonCodes] = useState<FeedbackReasonCode[]>([]);
  const [comment, setComment] = useState("");
  const [thanksTimer, setThanksTimer] = useState<number | null>(null);
  const [pendingVote, setPendingVote] = useState<"up" | "down" | null>(null);

  // Hydrate the popover form when the persisted feedback changes (e.g. after
  // someone else votes, or the user reopens the popover).
  useEffect(() => {
    if (feedback?.vote === "down") {
      setReasonCodes(feedback.reasonCodes ?? []);
      setComment(feedback.comment ?? "");
    } else {
      setReasonCodes([]);
      setComment("");
    }
  }, [feedback]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  const invalidateFeedback = () => {
    if (!feedbackContext?.threadId) return;
    qc.invalidateQueries({
      queryKey: trpc.salesTeams.listMessageFeedback.queryKey({
        businessId: feedbackContext.businessId,
        threadId: feedbackContext.threadId,
      }),
    });
  };

  const buildSynthetic = (vote: "up" | "down"): FeedbackRecord => ({
    messageId,
    threadId: feedbackContext?.threadId ?? "",
    businessId: feedbackContext?.businessId ?? "",
    vote,
    reasonCodes: vote === "down" ? reasonCodes : [],
    comment: vote === "down" && comment.trim() ? comment.trim() : null,
    topicKey: feedbackContext?.topicKey ?? "",
    scope: feedbackContext?.scope.kind ?? "business",
    salesTeamId:
      feedbackContext?.scope.kind === "team"
        ? feedbackContext.scope.salesTeamId
        : feedbackContext?.scope.kind === "member"
          ? feedbackContext.scope.salesTeamId
          : null,
    memberId:
      feedbackContext?.scope.kind === "member"
        ? feedbackContext.scope.memberId
        : null,
    promptVersion: metadata?.promptVersion ?? null,
    modelId: metadata?.modelId ?? null,
    contextFingerprint: metadata?.contextFingerprint ?? null,
    userId: "self",
    createdAt: optimistic?.createdAt ?? new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  const submitVote = (
    vote: "up" | "down",
    overrides?: { reasonCodes?: FeedbackReasonCode[]; comment?: string | null },
  ) => {
    if (!feedbackContext || !feedbackContext.threadId) return;
    const synthetic = buildSynthetic(vote);
    if (overrides?.reasonCodes) synthetic.reasonCodes = overrides.reasonCodes;
    if (overrides?.comment !== undefined) synthetic.comment = overrides.comment;
    setOptimistic(synthetic);
    setPendingVote(vote);
    submit.mutate(
      {
        businessId: feedbackContext.businessId,
        threadId: feedbackContext.threadId,
        messageId,
        vote,
        reasonCodes: synthetic.reasonCodes,
        comment: synthetic.comment,
        topicKey: feedbackContext.topicKey,
        scope: synthetic.scope,
        salesTeamId: synthetic.salesTeamId ?? null,
        memberId: synthetic.memberId ?? null,
        promptVersion: synthetic.promptVersion ?? null,
        modelId: synthetic.modelId ?? null,
        contextFingerprint: synthetic.contextFingerprint ?? null,
        runId: metadata?.runId ?? null,
      },
      {
        onSuccess: () => {
          track({
            event: LogEvents.IntelligenceFeedbackSubmitted.name,
            channel: LogEvents.IntelligenceFeedbackSubmitted.channel,
            vote,
            topic_key: feedbackContext.topicKey,
            scope: synthetic.scope,
            has_reason: synthetic.reasonCodes.length > 0,
            // "Thumbs up/down" (GRA-217): `target`/`value` match the shape
            // used by `Insight Feedback`; `vote` stays as-is for continuity
            // with this event's existing history.
            target: "chat",
            value: vote,
            role: memberRole,
            is_manager: isManager,
          });
        },
        onSettled: () => {
          setPendingVote(null);
          invalidateFeedback();
        },
        onError: () => setOptimistic(feedback ?? null),
      },
    );
  };

  const triggerThanks = () => {
    if (thanksTimer != null) window.clearTimeout(thanksTimer);
    const t = window.setTimeout(() => setThanksTimer(null), 1500);
    setThanksTimer(t);
  };

  const onClickThumb = (next: "up" | "down") => {
    if (!feedbackContext || !feedbackContext.threadId) return;
    if (optimistic?.vote === next) {
      const previous = optimistic;
      setOptimistic(null);
      clear.mutate(
        {
          businessId: feedbackContext.businessId,
          threadId: feedbackContext.threadId,
          messageId,
        },
        {
          onSettled: invalidateFeedback,
          onError: () => setOptimistic(previous),
        },
      );
      if (next === "down") setReasonOpen(false);
      return;
    }
    if (next === "down") {
      setReasonOpen(true);
      submitVote("down");
    } else {
      submitVote("up");
      triggerThanks();
    }
  };

  const onSubmitReason = () => {
    if (!feedbackContext) return;
    submitVote("down", {
      reasonCodes,
      comment: comment.trim() ? comment.trim() : null,
    });
    setReasonOpen(false);
  };

  const toggleReason = (code: FeedbackReasonCode) => {
    setReasonCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const upActive = optimistic?.vote === "up";
  const downActive = optimistic?.vote === "down";
  const disabled = !feedbackContext?.threadId;

  return (
    <div
      className={cn(
        "-ml-1 flex items-center gap-0.5 text-muted-foreground opacity-0 transition-opacity",
        "group-hover/msg:opacity-100 sm:opacity-0 sm:group-hover/msg:opacity-100",
      )}
    >
      <Button
        size="icon"
        variant="ghost"
        className="h-7 w-7"
        onClick={copy}
        aria-label={t("message.feedback.copy")}
        title={t("message.feedback.copy")}
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-emerald-600" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </Button>
      <Button
        size="icon"
        variant="ghost"
        disabled={disabled}
        className={cn(
          "h-7 w-7",
          upActive && "text-emerald-600 hover:text-emerald-600",
        )}
        onClick={() => onClickThumb("up")}
        aria-label={t("message.feedback.good")}
        title={
          disabled ? t("message.feedback.saveToVote") : t("message.feedback.good")
        }
      >
        {pendingVote === "up" ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <ThumbsUp className="h-3.5 w-3.5" />
        )}
      </Button>

      <Popover
        open={reasonOpen}
        onOpenChange={(o) => {
          setReasonOpen(o);
          if (!o && downActive) {
            // Closing without submitting keeps the bare 👎 row, so do nothing.
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            disabled={disabled}
            className={cn(
              "h-7 w-7",
              downActive && "text-destructive hover:text-destructive",
            )}
            onClick={() => onClickThumb("down")}
            aria-label={t("message.feedback.bad")}
            title={
              disabled ? t("message.feedback.saveToVote") : t("message.feedback.bad")
            }
          >
            {pendingVote === "down" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ThumbsDown className="h-3.5 w-3.5" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-3">
          <div className="space-y-3">
            <div className="text-sm font-medium">
              {t("message.feedback.whatWrong")}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {reasonChoices.map((r) => {
                const active = reasonCodes.includes(r.code);
                return (
                  <button
                    key={r.code}
                    type="button"
                    onClick={() => toggleReason(r.code)}
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-xs transition-colors",
                      active
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-background hover:bg-muted",
                    )}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("message.feedback.commentPlaceholder")}
              className="min-h-[72px] resize-none text-sm"
              maxLength={2000}
            />
            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReasonOpen(false)}
              >
                {t("message.feedback.close")}
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={onSubmitReason}
                disabled={submit.isPending}
              >
                {t("message.feedback.submit")}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {thanksTimer != null && (
        <span className="ml-1 text-xs text-emerald-600">
          {t("message.feedback.thanks")}
        </span>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            aria-label={t("message.feedback.more")}
            title={t("message.feedback.more")}
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-44">
          <DropdownMenuItem
            disabled={!onDelete}
            onClick={onDelete}
            className={cn(onDelete && "text-destructive focus:text-destructive")}
          >
            <Trash2 className="h-3.5 w-3.5" /> {t("chat.deleteThread")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

